import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faTimes } from "@fortawesome/free-solid-svg-icons";
import { format } from "date-fns";

import api from "../../api/http";

// ✅ Refolosește același CSS ca DeleteComments (recomandat)
import "../../styles/admin/Modal-coments-rating.css";

const DeleteRating = ({ productId, onChanged }) => {
  const [ratings, setRatings] = useState([]);
  const [userDetails, setUserDetails] = useState({}); // { [userId]: profile }
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState("");

  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const parseApiError = useCallback((err) => {
    const data = err?.response?.data;
    if (!data) return err?.message || "A apărut o eroare.";
    if (typeof data === "string") return data;
    if (Array.isArray(data)) return data[0] || "A apărut o eroare.";
    if (typeof data?.detail === "string") return data.detail;

    if (typeof data === "object") {
      const k = Object.keys(data)[0];
      const v = data[k];
      if (Array.isArray(v)) return v[0];
      if (typeof v === "string") return v;
      return JSON.stringify(data);
    }

    return "A apărut o eroare.";
  }, []);

  const normalizeList = useCallback((data) => {
    if (Array.isArray(data?.results)) return data.results;
    if (Array.isArray(data)) return data;
    return [];
  }, []);

  const makeAvatarUrl = useCallback((avatar) => {
    if (!avatar || typeof avatar !== "string") return "";
    if (avatar.startsWith("http://") || avatar.startsWith("https://")) return avatar;

    const base = api?.defaults?.baseURL || "";
    if (!base) return avatar;

    if (base.endsWith("/") && avatar.startsWith("/")) return base.slice(0, -1) + avatar;
    if (!base.endsWith("/") && !avatar.startsWith("/")) return base + "/" + avatar;
    return base + avatar;
  }, []);

  const displayName = useCallback((u) => {
    const first = String(u?.first_name || "").trim();
    const last = String(u?.last_name || "").trim();
    const full = `${first} ${last}`.trim();
    return full || "Unknown User";
  }, []);

  const parseBackendDate = useCallback((v) => {
    if (!v) return null;
    const s = String(v).trim().replace(" ", "T");
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  }, []);

  const fetchUserInfo = useCallback(async (userId, signal) => {
    const res = await api.get(`/users/profiles/${userId}/`, { signal });
    return res.data;
  }, []);

  // -----------------------
  // Fetch ratings
  // -----------------------
  const fetchRatings = useCallback(
    async (signal) => {
      setError("");
      setLoading(true);
      try {
        const res = await api.get(`/produs/ratings-list/${productId}`, { signal });
        const list = normalizeList(res.data);
        if (!mountedRef.current) return;
        setRatings(list);
      } catch (err) {
        if (!mountedRef.current) return;
        setError(parseApiError(err));
        setRatings([]);
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    },
    [normalizeList, parseApiError, productId]
  );

  useEffect(() => {
    if (!productId) return;
    const controller = new AbortController();
    fetchRatings(controller.signal);
    return () => controller.abort();
  }, [productId, fetchRatings]);

  // -----------------------
  // Fetch missing users (cache)
  // -----------------------
  useEffect(() => {
    if (!ratings.length) return;

    const controller = new AbortController();
    const { signal } = controller;

    const run = async () => {
      const ids = Array.from(new Set(ratings.map((r) => r.user_id).filter(Boolean)));
      const missing = ids.filter((id) => userDetails[id] === undefined);
      if (!missing.length) return;

      try {
        const results = await Promise.all(
          missing.map(async (id) => {
            try {
              const profile = await fetchUserInfo(id, signal);
              return [id, profile];
            } catch {
              return [id, undefined];
            }
          })
        );

        if (!mountedRef.current) return;

        setUserDetails((prev) => {
          const next = { ...prev };
          for (const [id, profile] of results) {
            if (profile !== undefined) next[id] = profile;
          }
          return next;
        });
      } catch {
        // ignore (abort)
      }
    };

    run();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ratings, fetchUserInfo]);

  // -----------------------
  // Delete rating
  // -----------------------
  const handleDeleteRating = useCallback(
    async (ratingId) => {
      setError("");

      const ok = window.confirm("Sigur vrei să ștergi acest rating?");
      if (!ok) return;

      setDeletingId(ratingId);
      try {
        await api.delete(`/produs/car/${productId}/rating/${ratingId}`);

        setRatings((prev) => prev.filter((r) => r.id !== ratingId));
        onChanged?.();
      } catch (err) {
        setError(parseApiError(err));
      } finally {
        if (mountedRef.current) setDeletingId(null);
      }
    },
    [onChanged, parseApiError, productId]
  );

  // newest first
  const sortedRatings = useMemo(() => {
    return [...ratings].sort((a, b) => {
      const da = parseBackendDate(a.created_at)?.getTime() ?? 0;
      const db = parseBackendDate(b.created_at)?.getTime() ?? 0;
      return db - da;
    });
  }, [ratings, parseBackendDate]);

  if (loading) {
    return <div className="srComList_loading">Loading ratings...</div>;
  }

  return (
    <div className="srComList_wrap">
      {error && <div className="srComList_error">{error}</div>}

      {sortedRatings.length === 0 ? (
        <div className="srComList_empty">Nu există ratings pentru acest produs.</div>
      ) : (
        <ul className="srComList_list">
          {sortedRatings.map((rating) => {
            const uRaw = userDetails[rating.user_id] || null;
            const name = displayName(uRaw);
            const avatar = makeAvatarUrl(uRaw?.avatar);
            const d = parseBackendDate(rating.created_at);
            const isDeleting = deletingId === rating.id;

            return (
              <li key={rating.id} className="srComList_item">
                <div className="srComList_left">
                  <div className="srComList_avatarWrap">
                    {avatar ? (
                      <img
                        src={avatar}
                        alt={`Avatar pentru ${name}`}
                        className="srComList_avatarImg"
                      />
                    ) : (
                      <FontAwesomeIcon icon={faUser} className="srComList_avatarIcon" />
                    )}
                  </div>

                  <div className="srComList_meta">
                    <div className="srComList_nameRow">
                      <div className="srComList_name">{name}</div>
                      <div className="srComList_date">
                        {d ? format(d, "dd.MM.yyyy HH:mm") : ""}
                      </div>
                    </div>

                    <div className="srComList_text">
                      Rating: <strong>{rating.rating}</strong>
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  className="srComModal_closeBtn"
                  title={isDeleting ? "Se șterge..." : "Șterge"}
                  onClick={isDeleting ? undefined : () => handleDeleteRating(rating.id)}
                  disabled={isDeleting}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default DeleteRating;